/**
 * @module app.cd
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import * as CD from 'app/cd/models/essette-api';

export interface IAuthSearchFilters {
    general?: string;
    memberId?: string | null;
    detailFilter?: string | null;
}

/**  Provides access to API models related to CD Auths */
@transient()
@autoinject()
export class AuthService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(private readonly api: Api, private readonly logger: AppLogger) { }

    ////****************** Queries **************************************************////

    /**
     * Load all `CD.Auth` models
     * @param options query load options
     * @param includes related models to include
     */
    public async loadAuths(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Auth>> {
        const query = this.api
            .query('cd/auths')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async searchImportedAuths(importedAuthsFilters: IAuthSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Auth>> {
        const query = this.api
            .query(`cd/auths/`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(importedAuthsFilters.memberId) {
            query.filterIdentifier('member.id', importedAuthsFilters.memberId)
        }
        if(importedAuthsFilters.detailFilter) {
            // Use an or search to look at various diagnoses, service, and medication data
            query.filterOr(query => query
                .filterExists('services', (f =>
                    f.filterString('code', importedAuthsFilters.detailFilter!, 'contains')
                ))
                .filterExists('services', (f =>
                    f.filterString('description', importedAuthsFilters.detailFilter!, 'contains')
                ))
                .filterExists('diagnoses', (f =>
                    f.filterString('code', importedAuthsFilters.detailFilter!, 'contains')
                ))
                .filterExists('diagnoses', (f =>
                    f.filterString('description', importedAuthsFilters.detailFilter!, 'contains')
                ))
                .filterExists('drugs', (f =>
                    f.filterString('code', importedAuthsFilters.detailFilter!, 'contains')
                ))
                .filterExists('drugs', (f =>
                    f.filterString('description', importedAuthsFilters.detailFilter!, 'contains')
                )));
        }
        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    /**
     * Gets a specific `CD.Auth` record
     * @param authId The id of the CD.Auth record to get
     * @param includes related models to include
     */
    public async loadAuth(authId: string, includes: string[] = []): Promise<IApiQueryResults<CD.Auth>> {
        const query = this.api
            .query(`cd/auths/${authId}`)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadAuthDiagnoses(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Diagnosis>> {
        const query = this.api
            .query(`cd/auths/${authId}/diagnoses`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }


    public async loadAuthServices(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Service>> {
        const query = this.api
            .query(`cd/auths/${authId}/services`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async loadAuthMedications(authId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.Drug>> {
        const query = this.api
            .query(`cd/auths/${authId}/drugs`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////

}
